import type { UiText } from '@ory/client';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { useShallow } from 'zustand/shallow';

import Icon from '@/components/Icon/Icon';
import VStack from '@/components/Layout/VStack';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { useSsoKratosTranslation } from '@/features/account/components/SsoAccountLink';
import { translateUiText } from '@/features/auth/hooks/useNodeTranslation';
import { useAuthStore } from '@/lib/auth';
import {
  ssoSettingsService,
  SUPPORTED_OIDC_PROVIDERS,
  type SsoCredentialItem,
} from '@/lib/auth/ory/service';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { CredentialCard } from './-components/CredentialCard';

const SearchSchema = z.object({
  flow: z.string().optional(),
});

export const Route = createFileRoute('/account/sso/')({
  validateSearch: zodValidator(SearchSchema),
  loaderDeps: ({ search }) => ({ flowId: search.flow }),
  loader: async ({ deps }) => {
    if (!deps.flowId) {
      throw redirect({ to: '/' });
    }

    try {
      const flow = await ssoSettingsService.getFlow(deps.flowId);
      const isFlowSuccess = flow.state === 'success';

      const errorUiTexts =
        flow.ui.messages?.filter((msg) => msg.type === 'error') ?? [];

      let credentials: SsoCredentialItem[] = [];
      if (isFlowSuccess) {
        try {
          credentials = await ssoSettingsService.getSsoCredentials();
        } catch {
          /* credential 取得失敗時は空配列のまま */
        }
      }

      return { isFlowSuccess, errorUiTexts, credentials };
    } catch (error) {
      console.warn('Failed to load SSO settings flow:', error);
      return {
        isFlowSuccess: false,
        errorUiTexts: [] as UiText[],
        credentials: [] as SsoCredentialItem[],
      };
    }
  },
  component: SsoCallbackPage,
});

function SsoCallbackPage() {
  const { t, i18n } = useTranslation(defaultNS, {
    keyPrefix: 'pages.accountSettings.sso',
  });
  const { isFlowSuccess, errorUiTexts, credentials } = Route.useLoaderData();
  const navigate = useNavigate();
  const session = useAuthStore(useShallow(({ session }) => session));

  const { ssoAwareT, language } = useSsoKratosTranslation();

  const flowErrorMessages = errorUiTexts.map((uiText) =>
    translateUiText(uiText, ssoAwareT, language),
  );

  const hasMultipleOrgs = (session?.switchableOrganizations?.length ?? 0) > 0;

  const resultConfig = isFlowSuccess
    ? {
        iconName: 'Check' as const,
        iconContainerClass: 'bg-success-muted-fill',
        iconClass: 'text-success-on-muted-fill',
        message: t('callbackSuccess'),
      }
    : {
        iconName: 'XIcon' as const,
        iconContainerClass: 'bg-error-muted-fill',
        iconClass: 'text-error-on-muted-fill',
        message: t('callbackError'),
      };

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-fill">
      <VStack className="w-full max-w-md items-center gap-6 rounded-lg border border-neutral-border bg-neutral-on-neutral-fill p-8">
        <div
          className={cn(
            'flex h-12 w-12 items-center justify-center rounded-full',
            resultConfig.iconContainerClass,
          )}
        >
          <Icon
            iconName={resultConfig.iconName}
            className={cn('h-6 w-6', resultConfig.iconClass)}
          />
        </div>
        <Text variant="h3" className="text-neutral-on-fill">
          {t('callbackTitle')}
        </Text>
        <Text variant="p" className="text-center text-neutral-muted-on-fill">
          {resultConfig.message}
        </Text>

        {isFlowSuccess && credentials.length > 0 && (
          <VStack className="w-full gap-3">
            {credentials.map((cred) => {
              const providerConfig = SUPPORTED_OIDC_PROVIDERS.find(
                (p) => p.provider === cred.provider,
              );
              return (
                <CredentialCard
                  key={cred.provider}
                  credential={cred}
                  language={i18n.language}
                  providerIconName={providerConfig?.iconName}
                />
              );
            })}
            {hasMultipleOrgs && (
              <Text
                variant="pSmall"
                className="text-center text-neutral-muted-on-fill"
              >
                {t('callbackMultiOrgNote')}
              </Text>
            )}
          </VStack>
        )}

        {!isFlowSuccess && flowErrorMessages.length > 0 && (
          <VStack className="w-full gap-2 rounded-md bg-error-muted-fill p-3">
            {flowErrorMessages.map((msg, index) => (
              <Text
                key={`${errorUiTexts[index]?.id ?? index}`}
                variant="pSmall"
                className="text-error-on-muted-fill"
              >
                {msg}
              </Text>
            ))}
          </VStack>
        )}
        <Button variant="default" onClick={() => navigate({ to: '/' })}>
          {t('callbackBackButton')}
        </Button>
      </VStack>
    </div>
  );
}
